import { call, put, select, takeLatest, takeEvery, all } from 'redux-saga/effects';
import { notify } from '@mrlannigan/reapop';
import { push } from 'connected-react-router';
import { omit } from 'lodash';
import { sagaError } from 'helpers/handleGenericSagaError';
import { parseLocationUrl, getParsedLocation } from 'services/router/selectors';
import { getOktaLoginUrl } from 'services/config/selectors';
import { fetchCurrentUser } from 'services/users/duck';
import { setCookie, deleteCookie } from '../../helpers/cookie';


import {
    actions,
    clearAuthToken,
    loginSuccess,
    saveAuthToken,
    submitLoginForm,
    submitResetPasswordForm,
    submitSetPasswordForm,
    submitSetPasswordSuccess
} from './duck';

import {
    accountConfirmEndpoint,
    loginEndpoint,
    oktaLoginEndpoint,
    oktaToken,
    passwordResetEndpoint
} from './selectors';

export function* sendLoginInformation(api, action) {
    try {
        const result = yield call(
            [api, api.request],
            {
                method: 'post',
                url: yield select(loginEndpoint),
                data: {
                    email: action.payload.email,
                    password: action.payload.password
                }
            }
        );

        const { token } = result.data;

        if (!token) {
            throw new Error('Missing token from response');
        }

        yield put(saveAuthToken({ token }));

        yield put(submitLoginForm.success());
        yield put(notify({
            id: action.payload.transactionId,
            message: 'Successful login',
            status: 'success'
        }));
        yield put(loginSuccess());

        const { search } = yield select(parseLocationUrl);

        yield put(fetchCurrentUser({ disableNavigation: !!search.from }));

        if (search.from) {
            yield put(push(search.from, omit(search, ['from'])));
        }
    } catch (err) {
        yield put(clearAuthToken({ hideSuccess: true }));
        yield put(submitLoginForm.failure(err));
        yield put(notify({
            id: action.payload.transactionId,
            ...sagaError(err)
        }));
    }
}

export function* setAccountPassword(api, action) {
    let payload = {};

    try {
        const { search } = yield select(parseLocationUrl);
        payload = {
            password: action.payload.password,
            confirmPassword: action.payload.confirmPassword,
            accessToken: search.token,
            transactionId: `confirm-${search.token.slice(0, 10)}` // security: not using full token so its not leaked
        };

        const result = yield call(
            [api, api.post],
            yield select(accountConfirmEndpoint),
            payload
        );

        yield put(submitSetPasswordForm.success());
        yield put(submitSetPasswordSuccess({ token: search.token }));
        yield put(notify({
            id: payload.transactionId,
            message: 'Password Set!',
            status: result.status
        }));
        yield put(push('/login'));
    } catch (err) {
        yield put(submitSetPasswordForm.failure(err));
        yield put(notify({
            id: payload.transactionId,
            ...sagaError(err)
        }));
    }
}

export function* resetAccountPassword(api, action) {
    const transactionId = `password-reset-${action.payload.email}`;

    try {
        const payload = {
            email: action.payload.email,
            transactionId
        };

        const result = yield call(
            [api, api.post],
            yield select(passwordResetEndpoint),
            payload
        );

        yield put(submitResetPasswordForm.success());
        yield put(notify({
            id: transactionId,
            message: 'Password reset sent!',
            status: result.status
        }));
        yield put(push('/login'));
    } catch (err) {
        yield put(submitResetPasswordForm.failure(err));
        yield put(notify({
            id: transactionId,
            ...sagaError(err)
        }));
    }
}

export function* saveAuthTokenSaga(api, action) {
    try {
        localStorage.setItem('authToken', action.payload.token);
        // setting this auth token cookie at parent domain to allow authentication
        // for apps on other subdomains
        setCookie('authToken', action.payload.token, 'goodwaygroup.com');
    } catch (err) {
        yield put(notify({
            id: action.payload.transactionId,
            message: 'Unable to store authentication token',
            status: 'error'
        }));
    }
}

export function* clearAuthTokenSaga(api, action) {
    try {
        localStorage.removeItem('authToken');
        deleteCookie('authToken', 'goodwaygroup.com');

        yield put(push('/login'));

        if (!action.payload || !action.payload.hideSuccess) {
            yield put(notify({
                message: 'Logged out successfully',
                status: '200'
            }));
        }
    } catch (err) {
        yield put(notify({
            message: 'Unable to remove authentication token',
            status: 'error'
        }));
    }
}

export function* loginOktaToken(api, action) {
    const { transactionId } = (action.payload || {});
    try {
        const result = yield call(
            [api, api.request],
            {
                method: 'post',
                url: yield select(oktaLoginEndpoint),
                headers: {
                    auth: yield select(oktaToken)
                }
            }
        );

        const { token } = result.data;

        if (!token) {
            throw new Error('Missing token from response');
        }

        yield put(saveAuthToken({ token }));

        yield put(notify({
            id: transactionId,
            message: 'Successful Okta login',
            status: 'success'
        }));

        const { search } = yield select(parseLocationUrl);

        yield put(fetchCurrentUser({ disableNavigation: !!search.from }));

        if (search.from) {
            yield put(push(search.from, omit(search, ['from'])));
        }
    } catch (err) {
        yield put(notify({
            id: transactionId,
            ...sagaError(err)
        }));
    }
}

export function* navigateToOktaBroker() {
    try {
        const parsedUrl = yield select(getParsedLocation);
        parsedUrl.set('pathname', '/admin/okta-receive');

        window.location.href = yield select(getOktaLoginUrl, parsedUrl.toString());
    } catch (err) {
        yield put(notify({
            message: 'Unable to navigate to Okta',
            status: 'error'
        }));
    }
}

export default function* authSagas({ api }) {
    yield all([
        takeLatest(submitLoginForm.REQUEST, sendLoginInformation, api),
        takeLatest(submitSetPasswordForm.REQUEST, setAccountPassword, api),
        takeLatest(submitResetPasswordForm.REQUEST, resetAccountPassword, api),
        takeLatest(actions.SAVE_AUTH_TOKEN, saveAuthTokenSaga, api),
        takeEvery(actions.CLEAR_AUTH_TOKEN, clearAuthTokenSaga, api),
        takeEvery(actions.LOGIN_OKTA_TOKEN, loginOktaToken, api),
        takeEvery(actions.NAVIGATE_TO_OKTA_BROKER, navigateToOktaBroker, api),
    ]);
}



// WEBPACK FOOTER //
// ./src/services/auth/saga.js